/* OMNIMatch AI — Sourcing Intelligence Engine (v3.9.3)
 *
 * The AI-enabled fuzzy-mapping workbench for Infosys Aggregator Category
 * Management. Uploads a customer's dirty ERP past-purchase spreadsheet,
 * matches each row against the UniBuy™ 30M-SKU hosted catalog, and
 * returns an enriched Excel with matched Mfg identity, confidence tier
 * (100 / 80 / 60 / None), and sourceable-spend summary.
 *
 * URL: /infoshop/admin/enrichment  (kept for backward compat; feature
 * canonicalised as "OMNIMatch AI" in v3.9.3 — was "AI Data Enrichment"
 * pre-v3.9.0, "OMNIMatch · Sourcing Intelligence" in v3.9.0/v3.9.2).
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload,
  Sparkles,
  Download,
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  Brain,
  FileSpreadsheet,
  Loader2,
  Zap,
  ShieldCheck,
  ShoppingCart,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/infoshop/admin/omnimatch`;

const REQUIRED_COLUMNS = [
  "Customer Name",
  "Customer Site Name",
  "Customer Site Country",
  "Item Number",
  "Vendor Name",
  "Vendor Part Number",
  "UoM",
  "Last Purchase Date",
  "Quantity Purchased",
  "MoQ",
  "Unit Price",
  "Currency",
  "Mfg Name",
  "Mfg Part Number",
  "Description",
];

const TIER_STYLES = {
  100: { bg: "bg-emerald-50", text: "text-emerald-800", border: "border-emerald-200", label: "100% match" },
  80: { bg: "bg-sky-50", text: "text-sky-800", border: "border-sky-200", label: "80% match" },
  60: { bg: "bg-amber-50", text: "text-amber-800", border: "border-amber-200", label: "60% match" },
  None: { bg: "bg-rose-50", text: "text-rose-800", border: "border-rose-200", label: "No match" },
};

function fmtMoney(n, ccy = "EUR") {
  if (n == null) return "—";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy, maximumFractionDigits: 2 }).format(n);
  } catch {
    return `${ccy} ${Number(n).toFixed(2)}`;
  }
}

export default function EnrichmentPage() {
  // Session bootstrap — demo mode always available via ?demo=1
  const [session, setSession] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [jobName, setJobName] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [unknownHeaders, setUnknownHeaders] = useState([]);
  const [stagingCart, setStagingCart] = useState(false);

  // Stage tier-100 (highest-confidence) OMNIMatch matches directly into
  // the buyer's UniBuy™ cart so Category Mgmt can side-by-side compare
  // vendor list-price vs UniBuy™ selling price in one click. Cart cap
  // is 6 unique lines per PRD §16, so we take the first 6 by match_score
  // descending. Skips lines already in the cart (backend upserts qty).
  const stageTier100ToCart = async (tier100) => {
    if (!tier100 || tier100.length === 0 || !session) return;
    const ranked = [...tier100]
      .sort((a, b) => (b.match_score ?? 0) - (a.match_score ?? 0))
      .slice(0, 6);
    setStagingCart(true);
    let staged = 0;
    let skipped = 0;
    for (const r of ranked) {
      try {
        await axios.post(
          `${BACKEND_URL}/api/infoshop/cart`,
          { infoshop_pn: r.matched_infoshop_pn, quantity: 1 },
          {
            params: {
              network_id: session.network_id,
              company_name: session.company_name,
            },
          }
        );
        staged += 1;
      } catch (err) {
        skipped += 1;
        console.warn("[OMNIMatch] stage-to-cart failed for", r.matched_infoshop_pn, err);
      }
    }
    setStagingCart(false);
    if (staged > 0) {
      toast.success(
        `Staged ${staged} tier-100 match${staged === 1 ? "" : "es"} into cart` +
        (skipped ? ` · ${skipped} skipped` : "") +
        (tier100.length > 6 ? ` · showing first 6 of ${tier100.length} (cart cap)` : "")
      );
    } else {
      toast.error("Could not stage any tier-100 matches into the cart");
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setSession({
      network_id: params.get("network_id") || "INFOSYS.ADMIN",
      company_name: params.get("company_name") || "INFOSYS BPM",
      demo: params.get("demo") === "1" ? 1 : 0,
    });
  }, []);

  const authParams = useCallback(
    () => (session ? { network_id: session.network_id, company_name: session.company_name, demo: session.demo } : null),
    [session]
  );

  const loadJobs = useCallback(async () => {
    if (!session) return;
    try {
      const { data } = await axios.get(`${API}/jobs`, { params: authParams() });
      setJobs(data.jobs || []);
    } catch (err) {
      console.warn("[OMNIMatch] load jobs failed:", err);
    }
  }, [session, authParams]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // Poll the active job while it's in-flight
  useEffect(() => {
    if (!activeJob || !session) return;
    if (activeJob.phase === "complete") return;
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/jobs/${activeJob.job_id}`, {
          params: { ...authParams(), include_results: 0 },
        });
        setActiveJob(data);
        if (data.phase === "complete") {
          loadJobs();
        }
      } catch (err) {
        console.warn("[OMNIMatch] poll failed:", err);
      }
    }, 1500);
    return () => clearInterval(t);
  }, [activeJob, session, authParams, loadJobs]);

  const downloadTemplate = async () => {
    if (!session) return;
    try {
      const { data } = await axios.get(`${API}/template`, {
        params: authParams(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "omnimatch-template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Template download failed");
    }
  };

  const onFilePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadFile(f);
    if (!jobName) setJobName(f.name.replace(/\.(xlsx|csv|xls)$/i, ""));
  };

  const submitUpload = async () => {
    if (!uploadFile || !session) return;
    setUploading(true);
    setUploadProgress(0);
    setUnknownHeaders([]);
    try {
      const form = new FormData();
      form.append("file", uploadFile);
      form.append("network_id", session.network_id);
      form.append("company_name", session.company_name);
      form.append("job_name", jobName || uploadFile.name);
      form.append("customer_name", customerName || "");
      form.append("demo", String(session.demo || 0));
      const { data } = await axios.post(`${API}/upload`, form, {
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      setUnknownHeaders(data.unknown_headers || []);
      toast.success(`Uploaded ${data.total_rows} rows · job ${data.job_id}`);
      // Kick off polling with a stub job doc
      const stub = {
        job_id: data.job_id,
        total_rows: data.total_rows,
        phase: "queued",
        progress_matched: 0,
        stats: null,
      };
      setActiveJob(stub);
      setUploadFile(null);
      loadJobs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const openJob = async (jobId) => {
    if (!session) return;
    try {
      const { data } = await axios.get(`${API}/jobs/${jobId}`, {
        params: { ...authParams(), include_results: 1 },
      });
      setActiveJob(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load job");
    }
  };

  const downloadEnriched = async (jobId) => {
    if (!session) return;
    try {
      const { data } = await axios.get(`${API}/jobs/${jobId}/download`, {
        params: authParams(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${jobId}-enriched.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed");
    }
  };

  const progressPct =
    activeJob && activeJob.total_rows
      ? Math.round(((activeJob.progress_matched || 0) / activeJob.total_rows) * 100)
      : 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft size={16} />
            <span className="text-sm font-semibold">Back</span>
          </Link>
          <div className="flex items-center gap-3">
            <div
              className="h-9 w-9 rounded-md flex items-center justify-center text-white font-bold shadow-md"
              style={{ background: "linear-gradient(135deg, #0072CE 0%, #7C3AED 100%)" }}
            >
              <Sparkles size={17} />
            </div>
            <div className="leading-tight">
              <div className="text-[10px] tracking-[0.22em] uppercase text-slate-500 font-bold">
                Infosys Aggregator · Category Mgmt
              </div>
              <div className="font-display font-bold text-slate-900 text-sm" data-testid="omnimatch-title">
                OMNIMatch AI · Sourcing Intelligence Engine
              </div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2 text-xs text-slate-500">
            <Brain size={13} className="text-purple-500" />
            Fuzzy · Gemini 3 Flash · Persistent Cache
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-6 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left rail — upload + template */}
        <aside className="lg:col-span-4 space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <FileSpreadsheet size={14} className="text-[#0072CE]" />
              <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
                Step 1 · Download template
              </span>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-xs text-slate-600 leading-relaxed">
                Use the mandated 15-column template — headers must match exactly (case &amp; spelling).
                Optional columns (<em>Mfg Name</em>, <em>Mfg Part Number</em>, <em>Description</em>) boost match tier when populated.
              </p>
              <Button
                onClick={downloadTemplate}
                data-testid="omnimatch-download-template"
                className="w-full bg-slate-900 hover:bg-slate-800 text-white text-xs"
              >
                <Download size={13} className="mr-2" /> Download OMNIMatch template
              </Button>
              <details className="text-[11px] text-slate-500">
                <summary className="cursor-pointer font-semibold text-slate-700">Required columns ({REQUIRED_COLUMNS.length})</summary>
                <ul className="mt-2 space-y-0.5 font-mono text-[10px] leading-relaxed">
                  {REQUIRED_COLUMNS.map((c) => (
                    <li key={c} className="text-slate-600">· {c}</li>
                  ))}
                </ul>
              </details>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <Upload size={14} className="text-[#0072CE]" />
              <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
                Step 2 · Upload
              </span>
            </div>
            <div className="p-4 space-y-3">
              <Input
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Customer (e.g. Danone, JDE, Unilever)"
                data-testid="omnimatch-customer-name"
                className="h-9 text-sm"
              />
              <Input
                value={jobName}
                onChange={(e) => setJobName(e.target.value)}
                placeholder="Job label (optional)"
                data-testid="omnimatch-job-name"
                className="h-9 text-sm"
              />
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">
                  XLSX or CSV (max 25 MB)
                </span>
                <input
                  type="file"
                  accept=".xlsx,.csv,.xls"
                  onChange={onFilePick}
                  data-testid="omnimatch-file-input"
                  className="block w-full text-xs text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-[#0072CE] file:text-white hover:file:bg-[#005BA6]"
                />
              </label>
              {uploadFile && (
                <div className="text-[11px] text-slate-500 bg-slate-50 rounded-md p-2 border border-slate-200">
                  <span className="font-mono">{uploadFile.name}</span> · {(uploadFile.size / 1024).toFixed(1)} KB
                </div>
              )}
              <Button
                onClick={submitUpload}
                disabled={!uploadFile || uploading}
                data-testid="omnimatch-upload-submit"
                className="w-full bg-gradient-to-r from-[#0072CE] to-purple-600 hover:opacity-90 text-white text-sm py-5 font-semibold"
              >
                {uploading ? (
                  <>
                    <Loader2 size={14} className="animate-spin mr-2" /> Uploading… {uploadProgress}%
                  </>
                ) : (
                  <>
                    <Zap size={14} className="mr-2" /> Run OMNIMatch pipeline
                  </>
                )}
              </Button>
              {unknownHeaders.length > 0 && (
                <div className="text-[11px] rounded-md bg-amber-50 border border-amber-200 text-amber-900 p-2 leading-snug">
                  <strong>Ignored columns:</strong> {unknownHeaders.join(", ")}
                </div>
              )}
            </div>
          </div>

          {/* Jobs history */}
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <ShieldCheck size={14} className="text-[#0072CE]" />
              <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
                Recent uploads
              </span>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {jobs.length === 0 && (
                <div className="p-4 text-xs text-slate-400 text-center">No uploads yet.</div>
              )}
              {jobs.map((j) => (
                <button
                  key={j.job_id}
                  onClick={() => openJob(j.job_id)}
                  data-testid={`omnimatch-job-${j.job_id}`}
                  className={`w-full text-left px-4 py-2.5 border-b border-slate-50 hover:bg-slate-50 ${
                    activeJob?.job_id === j.job_id ? "bg-[#0072CE]/5" : ""
                  }`}
                >
                  <div className="font-mono text-[11px] font-bold text-slate-800">{j.job_id}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {j.job_name} · {j.customer_name || "—"}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1.5">
                    {j.phase === "complete" ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700">
                        <CheckCircle2 size={9} /> {j.total_rows} rows
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-700">
                        <Loader2 size={9} className="animate-spin" /> {j.phase}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Right pane — active job */}
        <section className="lg:col-span-8">
          {!activeJob && (
            <div className="rounded-xl border-2 border-dashed border-slate-200 bg-white p-12 text-center">
              <Sparkles size={28} className="mx-auto text-slate-300" />
              <div className="mt-3 font-display font-semibold text-slate-700">
                Upload a customer past-purchase file to begin
              </div>
              <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto leading-relaxed">
                OMNIMatch will fuzzy-map every row against the UniBuy™ hosted catalog,
                escalate ambiguous rows to Gemini 3 Flash, and produce a downloadable
                enriched Excel with sourceable-spend summary.
              </p>
            </div>
          )}

          {activeJob && (
            <div className="space-y-5">
              {/* Header + progress */}
              <div className="rounded-xl bg-white border border-slate-200 p-5" data-testid="omnimatch-active-job">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-mono text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      {activeJob.job_id}
                    </div>
                    <div className="font-display text-xl font-bold text-slate-900 mt-0.5">
                      {activeJob.job_name || "Unnamed job"}
                    </div>
                    <div className="text-sm text-slate-500 mt-0.5">
                      {activeJob.customer_name || "No customer set"} · {activeJob.total_rows} rows
                    </div>
                  </div>
                  {activeJob.phase === "complete" && (
                    <Button
                      onClick={() => downloadEnriched(activeJob.job_id)}
                      data-testid="omnimatch-download-enriched"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                    >
                      <Download size={13} className="mr-2" /> Download enriched Excel
                    </Button>
                  )}
                </div>

                {/* Progress bar */}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-[11px] mb-1.5">
                    <span className="font-semibold text-slate-700">
                      {activeJob.phase === "complete"
                        ? "Complete"
                        : activeJob.phase === "layer_2_llm"
                        ? "Layer 2 · Gemini 3 Flash disambiguation"
                        : "Layer 1 · rapidfuzz matching"}
                    </span>
                    <span className="font-mono text-slate-500">
                      {activeJob.progress_matched || 0} / {activeJob.total_rows}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      data-testid="omnimatch-progress-bar"
                      className="h-full transition-all"
                      style={{
                        width: `${activeJob.phase === "complete" ? 100 : progressPct}%`,
                        background:
                          activeJob.phase === "complete"
                            ? "#10B981"
                            : "linear-gradient(90deg, #0072CE 0%, #7C3AED 100%)",
                      }}
                    />
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1.5 font-mono">
                    LLM calls: {activeJob.llm_calls || 0} · Cache hits: {activeJob.llm_cache_hits || 0}
                  </div>
                </div>
              </div>

              {/* Stats */}
              {activeJob.stats && activeJob.phase === "complete" && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="omnimatch-stats">
                  <StatCard
                    label="Total spend"
                    value={fmtMoney(
                      activeJob.stats.total_spend,
                      Object.keys(activeJob.stats.currencies || {})[0] || "EUR"
                    )}
                    accent="from-slate-800 to-slate-900"
                  />
                  <StatCard
                    label="High-confidence sourceable"
                    value={fmtMoney(
                      activeJob.stats.high_confidence_spend,
                      Object.keys(activeJob.stats.currencies || {})[0] || "EUR"
                    )}
                    hint={`${activeJob.stats.high_confidence_pct}% of total`}
                    accent="from-emerald-500 to-emerald-700"
                  />
                  <StatCard
                    label="Sourceable rows"
                    value={activeJob.stats.sourceable_row_count}
                    hint={`of ${activeJob.stats.total_rows}`}
                    accent="from-sky-500 to-sky-700"
                  />
                  <StatCard
                    label="Needs manual sourcing"
                    value={activeJob.stats.not_matched_row_count}
                    hint="unmatched rows"
                    accent="from-rose-500 to-rose-700"
                  />
                </div>
              )}

              {/* Tier distribution */}
              {activeJob.stats && activeJob.phase === "complete" && (
                <div className="rounded-xl bg-white border border-slate-200 p-4">
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">
                    Match tier distribution
                  </div>
                  <div className="space-y-2">
                    {["100", "80", "60", "None"].map((tier) => {
                      const count = (activeJob.stats.tier_counts || {})[tier] || 0;
                      const spend = (activeJob.stats.tier_spend || {})[tier] || 0;
                      const pct = activeJob.stats.total_rows ? (count / activeJob.stats.total_rows) * 100 : 0;
                      const style = TIER_STYLES[tier];
                      return (
                        <div key={tier} className="flex items-center gap-3" data-testid={`omnimatch-tier-${tier}`}>
                          <span
                            className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold ${style.bg} ${style.text} border ${style.border} rounded px-1.5 py-0.5 w-24 justify-center`}
                          >
                            {style.label}
                          </span>
                          <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className={`h-full ${tier === "100" ? "bg-emerald-500" : tier === "80" ? "bg-sky-500" : tier === "60" ? "bg-amber-500" : "bg-rose-500"}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="text-xs font-mono text-slate-700 w-24 text-right">
                            {count} · {fmtMoney(spend, Object.keys(activeJob.stats.currencies || {})[0] || "EUR")}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Results table */}
              {activeJob.results && activeJob.results.length > 0 && (
                <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
                      Per-row matches
                    </div>
                    <div className="flex items-center gap-3">
                      {(() => {
                        const tier100 = activeJob.results.filter(
                          (r) => String(r.match_tier || "") === "100" && r.matched_infoshop_pn
                        );
                        return (
                          <button
                            type="button"
                            onClick={() => stageTier100ToCart(tier100)}
                            disabled={tier100.length === 0 || stagingCart}
                            data-testid="omnimatch-convert-to-cart-btn"
                            title={
                              tier100.length === 0
                                ? "No 100%-tier matches available to stage"
                                : `Stage ${Math.min(tier100.length, 6)} of ${tier100.length} tier-100 match(es) into a comparison cart`
                            }
                            className="inline-flex items-center gap-2 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-xs font-semibold px-3 py-1.5 shadow-sm transition-colors"
                          >
                            {stagingCart ? (
                              <Loader2 size={13} className="animate-spin" />
                            ) : (
                              <ShoppingCart size={13} />
                            )}
                            Convert tier-100 to cart · {tier100.length}
                          </button>
                        );
                      })()}
                      <div className="text-[10px] text-slate-400">
                        Fuzzy: rapidfuzz · LLM: Gemini 3 Flash
                      </div>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                        <tr>
                          <th className="px-3 py-2 text-left">Vendor PN</th>
                          <th className="px-3 py-2 text-left">Description</th>
                          <th className="px-3 py-2 text-left">Matched UniBuy™ PN</th>
                          <th className="px-3 py-2 text-left">Mfg</th>
                          <th className="px-3 py-2 text-right">Tier</th>
                          <th className="px-3 py-2 text-right">Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeJob.results.slice(0, 200).map((r, i) => {
                          const tier = r.match_tier ? String(r.match_tier) : "None";
                          const s = TIER_STYLES[tier];
                          return (
                            <tr key={i} className="border-t border-slate-100" data-testid={`omnimatch-result-${i}`}>
                              <td className="px-3 py-2 font-mono text-[11px]">{r.row["Vendor Part Number"] || "—"}</td>
                              <td className="px-3 py-2 text-slate-700 truncate max-w-xs">{r.row.Description || "—"}</td>
                              <td className="px-3 py-2 font-mono text-[11px]">{r.matched_infoshop_pn || <span className="text-slate-400">—</span>}</td>
                              <td className="px-3 py-2 text-slate-700 truncate max-w-[10rem]">
                                {r.matched_mfg_name ? `${r.matched_mfg_name} · ${r.matched_mfg_pn}` : <span className="text-slate-400">—</span>}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <span className={`inline-flex text-[10px] uppercase tracking-wider font-bold ${s.bg} ${s.text} border ${s.border} rounded px-1.5 py-0.5`}>
                                  {s.label}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right font-mono text-slate-700">{r.match_score?.toFixed?.(1) ?? r.match_score ?? "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {activeJob.results.length > 200 && (
                    <div className="px-4 py-2 text-[11px] text-slate-500 border-t border-slate-100">
                      Showing 200 of {activeJob.results.length} rows — download the enriched Excel for the full list.
                    </div>
                  )}
                </div>
              )}

              {/* In-flight placeholder */}
              {activeJob.phase !== "complete" && !activeJob.results?.length && (
                <div className="rounded-xl bg-white border border-slate-200 p-8 text-center">
                  <Loader2 size={22} className="mx-auto text-slate-400 animate-spin" />
                  <div className="mt-3 text-sm font-semibold text-slate-700">
                    OMNIMatch pipeline running…
                  </div>
                  <div className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                    Rows are processed in batches of 100. Ambiguous lines escalate to Gemini 3
                    Flash — cached results skip the LLM entirely.
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </main>

      <footer className="max-w-7xl mx-auto px-4 lg:px-6 py-6 text-[11px] text-slate-400 flex items-center gap-1.5">
        <AlertTriangle size={11} />
        <span>
          Access is gated to Infosys Aggregator Category Mgmt (network_id starts with{" "}
          <code className="text-[10px] bg-slate-100 rounded px-1">INFOSYS.</code>, company{" "}
          <code className="text-[10px] bg-slate-100 rounded px-1">INFOSYS BPM</code>).
          Append <code className="text-[10px] bg-slate-100 rounded px-1">?demo=1</code> for prospect walkthroughs.
        </span>
      </footer>
    </div>
  );
}

function StatCard({ label, value, hint, accent }) {
  return (
    <div className="rounded-xl overflow-hidden shadow-sm">
      <div className={`bg-gradient-to-br ${accent} text-white px-4 py-3`}>
        <div className="text-[10px] uppercase tracking-wider font-bold text-white/80">{label}</div>
        <div className="text-xl font-display font-bold mt-0.5">{value}</div>
        {hint && <div className="text-[10px] text-white/70 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}
